export function initializeZephrConfig(global: typeof window): void {
  global.Zephr = global.Zephr || {};
  global.Zephr.includeOutcomes ??= true;
  global.Zephr.outcomesAsEvents ??= false;
  global.Zephr.groupFields ??= false;
}

export async function fetchZephrDataLayer(
  endpoint = "/blaize/datalayer",
): Promise<any | null> {
  const res = await fetch(endpoint);
  if (!res.ok) return null;
  return await res.json();
}

export function createOutcomeEvents(
  zephr: NonNullable<typeof window.Zephr>,
): Record<string, any>[] {
  const events: Record<string, any>[] = [];

  if (zephr.includeOutcomes && zephr.outcomes && zephr.outcomesAsEvents) {
    for (const [key, outcome] of Object.entries(zephr.outcomes)) {
      events.push({
        event: `zephr-outcome-${key}`,
        featureId: key,
        featureLabel: outcome.featureLabel,
        outcomeId: outcome.outcomeId,
        outcomeLabel: outcome.outcomeLabel,
      });
    }
  }

  return events;
}

export function enrichPageViewWithAccessDetails(
  pageView: Record<string, any>,
  zephr: NonNullable<typeof window.Zephr>,
): void {
  const { accessDetails } = zephr;
  if (!accessDetails) return;

  const {
    trials = {},
    trialTrackingDetails = [],
    timeTrials = {},
  } = accessDetails;

  for (const [_, trial] of Object.entries(trials)) {
    if (!trial.reportInDataLayer) continue;
    const used = trial.totalCredits - trial.remainingCredits;
    const target = zephr.groupFields ? (pageView.zephrTrials ??= {}) : pageView;
    if (trial.dataLayerCreditsUsedKey) {
      target[trial.dataLayerCreditsUsedKey] = used;
    }
    if (trial.dataLayerCreditsRemainingKey) {
      target[trial.dataLayerCreditsRemainingKey] = trial.remainingCredits;
    }
  }

  for (const details of trialTrackingDetails) {
    const type = details.entitlementType === "credits" ? "credits" : "meters";
    const trial = accessDetails[type]?.[details.entitlementId];
    if (!trial) continue;

    const target = zephr.groupFields ? (pageView.zephrTrials ??= {}) : pageView;
    if (details.creditsRemainingKey) {
      target[details.creditsRemainingKey] = trial.remainingCredits;
    }
    if (details.creditsUsedKey) {
      target[details.creditsUsedKey] =
        trial.totalCredits - trial.remainingCredits;
    }
  }

  for (const [_, trial] of Object.entries(timeTrials)) {
    if (!trial.reportInDataLayer) continue;
    const target = zephr.groupFields ? (pageView.zephrTrials ??= {}) : pageView;

    if (trial.dataLayerTrialDurationKey) {
      target[trial.dataLayerTrialDurationKey] = trial.trialDuration;
    }
    if (trial.dataLayerTrialDurationUnitsKey) {
      target[trial.dataLayerTrialDurationUnitsKey] = trial.trialDurationUnits;
    }
    if (trial.dataLayerTrackerEntryTimeKey) {
      target[trial.dataLayerTrackerEntryTimeKey] = trial.entryTime;
    }
    if (trial.dataLayerTrackerHitsKey) {
      target[trial.dataLayerTrackerHitsKey] = trial.trackerHits;
    }
    if (trial.dataLayerTrialTimeRemainingKey) {
      target[trial.dataLayerTrialTimeRemainingKey] = trial.timeRemainingInTrial;
    }
  }
}

export function dispatchZephrReadyEvent(): void {
  const evt = new Event("zephr.dataLayerReady", {
    bubbles: true,
    cancelable: true,
  });
  document.dispatchEvent(evt);
}

export async function initZephrDataLayer(): Promise<void> {
  initializeZephrConfig(window);
  try {
    const data = await fetchZephrDataLayer();
    if (!data) return;

    const keys = Object.keys(data);
    const values = Object.values(data);

    if (
      keys.length === 1 &&
      values[0] &&
      values[0].constructor === Object &&
      Object.keys(values[0]).length === 0
    ) {
      return;
    }

    const zephr = window.Zephr!;
    for (const field in data) {
      if (!(field in window)) window[field] = [];

      const pageView: Record<string, any> = data[field];
      const events: any[] = [];

      if (zephr.includeOutcomes && zephr.outcomes) {
        pageView.zephrOutcomes = zephr.outcomes;
      }

      events.push(...createOutcomeEvents(zephr));
      enrichPageViewWithAccessDetails(pageView, zephr);

      if (Object.keys(pageView).length > 0) {
        pageView.event = "zephr-pageview";
        events.unshift(pageView);
      }

      for (const event of events) {
        window[field].push(event);
      }
    }

    dispatchZephrReadyEvent();
  } catch (err) {
    console.error("Failed to load Zephr datalayer", err);
  }
}

export function getZephrFeatureOutcome(featureKey: string):
  | {
      featureLabel: string;
      outcomeId: string;
    }
  | undefined {
  const outcome = window.Zephr?.outcomes?.[featureKey];
  if (!outcome) return undefined;

  return {
    featureLabel: outcome.featureLabel,
    outcomeId: outcome.outcomeId,
  };
}

export function storeZephrOutcome(tenant: string) {
  const featureLabel =
    window.Zephr?.outcomes?.[`${tenant}-paywall`]?.featureLabel;
  const outcomeId = window.Zephr?.outcomes?.[`${tenant}-paywall`]?.outcomeId;

  if (featureLabel && outcomeId) {
    sessionStorage.setItem(
      "zephr-outcome",
      JSON.stringify({ featureLabel, outcomeId }),
    );
  }
}

export function rewriteUrl(
  path: string,
  envBaseUrl: string,
): string | undefined {
  if (!path) return;

  try {
    const newUrl = new URL(path);
    return newUrl.toString();
  } catch {
    try {
      const fullUrl = new URL(path, envBaseUrl);
      return fullUrl.toString();
    } catch (error) {
      console.warn("Invalid URL in rewriteUrl:", error);
      return undefined;
    }
  }
}

export function getFetcher(controller: AbortController) {
  return (url: string, options: RequestInit) => {
    return fetch(url, {
      ...options,
      signal: controller.signal,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json().then((data) => {
        return new Response(JSON.stringify(data), {
          status: response.status,
          statusText: response.statusText,
          headers: {
            ...Object.fromEntries(response.headers.entries()),
            "Content-Type": "application/json",
          },
        });
      });
    });
  };
}

export function logZephrError(
  error: Error,
  forwarderUrl: string,
  baseUrl: string,
) {
  const payload = {
    baseUrl,
    timestamp: new Date(),
    statusMessage: error.message,
    userAgent: navigator.userAgent.slice(0, 120),
  };

  fetch(`${forwarderUrl}/zephr-load-error`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.warn("Fehler beim Fehlerreporting:", err);
  });
}

export async function fetchZephrJWT(
  envBaseUrl: string,
  loggedIn: boolean,
): Promise<string | null> {
  if (!loggedIn) return null;

  const local = localStorage.getItem("zephr-key");
  if (local) {
    try {
      const parsed = JSON.parse(local);
      const expire = new Date(parsed.expire).getTime();
      if (expire > Date.now()) {
        return parsed.token;
      }
    } catch {
      localStorage.removeItem("zephr-key");
    }
  }

  try {
    const response = await fetch(`${envBaseUrl}/p-api/plenigo/zephr-token`);
    const json = await response.json();
    localStorage.setItem("zephr-key", JSON.stringify(json));
    return json.token;
  } catch {
    return null;
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Zephr: {
      includeOutcomes?: boolean;
      outcomesAsEvents?: boolean;
      groupFields?: boolean;
      outcomes?: Record<
        string,
        {
          featureLabel: string;
          outcomeId: string;
          outcomeLabel: string;
        }
      >;
      accessDetails?: {
        trials?: Record<
          string,
          {
            totalCredits: number;
            remainingCredits: number;
            reportInDataLayer?: boolean;
            dataLayerCreditsUsedKey?: string;
            dataLayerCreditsRemainingKey?: string;
          }
        >;
        trialTrackingDetails?: {
          entitlementType: string;
          entitlementId: string;
          creditsRemainingKey?: string;
          creditsUsedKey?: string;
        }[];
        credits?: Record<
          string,
          { totalCredits: number; remainingCredits: number }
        >;
        meters?: Record<
          string,
          { totalCredits: number; remainingCredits: number }
        >;
        timeTrials?: Record<
          string,
          {
            reportInDataLayer?: boolean;
            dataLayerTrialDurationKey?: string;
            dataLayerTrialDurationUnitsKey?: string;
            dataLayerTrackerEntryTimeKey?: string;
            dataLayerTrackerHitsKey?: string;
            dataLayerTrialTimeRemainingKey?: string;
            trialDuration?: number;
            trialDurationUnits?: string;
            entryTime?: number;
            trackerHits?: number;
            timeRemainingInTrial?: number;
          }
        >;
      };
    };
    [key: string]: any;
  }

  type JWTResponseBody = {
    status: string;
    expire: Date;
    token: string;
  };

  type WebLogForwarderObject = {
    baseUrl: string;
    timestamp: Date;
    statusMessage: string | null;
    userAgent: string;
  };
}
